<template>
  <div class="baggage-display">
    <div
      v-for="(itinerary, i) in priceResponse.itineraries"
      :key="i + 'itinerary'"
    >
      <div
        v-for="(leg, legIndex) in itinerary.odoList"
        :key="'leg-' + legIndex"
        class="breakdown bottom-30"
        style="margin-bottom: 30px"
      >
        <!-- Route Display -->
        <div class="pair">
          <span class="value">{{ getRouteDisplay(leg) }}</span>
        </div>

        <!-- Passenger Baggage Info -->
        <div
          v-for="(passenger, passengerIndex) in allPassengers"
          :key="`baggage-passenger-${passengerIndex}`"
          class="key"
        >
          <span>Passenger {{ passengerIndex + 1 }}:</span>
          <div
            v-for="(baggage, index) in getPassengerBaggage(passenger, leg)"
            :key="'baggage-' + index"
          >
            <span>{{ baggage }}</span>
          </div>
          <br />
        </div>
      </div>
    </div>
    <div v-if="hasCheckedBaggage">
      <div class="key">
        Please refer to your e-ticket for your confirmed baggage allowance.
      </div>
      <br />
    </div>
  </div>
</template>

<script>
import { mapState } from "vuex";

export default {
  name: "BaggageDisplay",
  props: {
    allPassengers: {
      type: Array,
      required: true,
    },
  },

  computed: {
    ...mapState([
      "priceResponse",
      "baggageCost",
      "additionalBaggageAllowed",
      "passengers",
    ]),

    flightHasPerPassengerBags() {
      return this.priceResponse.baggageOptions.allBaggage.length > 0;
    },

    hasCheckedBaggage() {
      return this.priceResponse.itineraries.some((itinerary) =>
        itinerary.odoList.some((leg) => {
          const baggageAllowance = leg.segments[0].baggageAllowance;

          if (!baggageAllowance || baggageAllowance.length === 0) {
            return false;
          }

          return baggageAllowance.some((bag) => this.isValidBag(bag));
        })
      );
    },
  },

  methods: {
    isValidBag(bag) {
      // Basic checks - empty or invalid bags
      if (!bag || !bag.trim()) {
        return false;
      }

      const bagLower = bag.toLowerCase();
      if (
        bagLower.includes("no baggage allowance") ||
        bagLower.includes("hand baggage")
      ) {
        return false;
      }

      // If it's purchasable baggage (contains "for"), check if user actually selected any
      if (bag.includes("for") && this.flightHasPerPassengerBags) {
        return this.allPassengers.some(
          (passenger) =>
            passenger.baggageSelection &&
            passenger.baggageSelection.some((selection) => selection.selected)
        );
      }

      // For included baggage, show if it contains checked baggage or kg (not hand)
      return (
        bagLower.includes("checked baggage") ||
        (bagLower.includes("kg") && !bagLower.includes("hand"))
      );
    },

    getRouteDisplay(leg) {
      const origin = leg.segments[0].origCode;
      const destination = leg.segments[leg.segments.length - 1].destCode;
      return `${origin} to ${destination}:`;
    },

    getPassengerBaggage(passenger, leg) {
      const baggageAllowance = leg.segments[0].baggageAllowance;
      const origCode = leg.segments[0].origCode;

      return baggageAllowance
        .map(
          function(bag) {
            // Standard baggage case
            if (!bag.includes("for")) {
              return `${bag}${this.getBaggageTypeText(bag)}`;
            }

            // No baggage cost case
            if (this.baggageCost === 0) {
              return `${
                this.additionalBaggageAllowed ? "Additional" : "Checked"
              } baggage not included`;
            }

            // Per passenger baggage case
            if (this.shouldShowPerPassengerBaggage(bag)) {
              const selectedBaggage = this.getSelectedBaggageDescription(
                passenger,
                origCode
              );

              if (selectedBaggage) {
                return `${selectedBaggage} ${
                  this.additionalBaggageAllowed ? "additional" : "checked"
                } baggage`;
              }

              return `${
                this.additionalBaggageAllowed ? "Additional" : "Checked"
              } baggage not included`;
            }

            return "";
          }.bind(this)
        )
        .filter(function(text) {
          return text && text.length > 0;
        });
    },

    getBaggageTypeText(bag) {
      return bag.includes("hand baggage") || bag.includes("No")
        ? ""
        : " checked baggage";
    },

    shouldShowPerPassengerBaggage(bag) {
      return (
        this.flightHasPerPassengerBags &&
        this.baggageCost > 0 &&
        bag.includes("for")
      );
    },

    getSelectedBaggageDescription(passenger, origCode) {
      if (!passenger || !passenger.baggageSelection) {
        return "";
      }

      const descriptions = passenger.baggageSelection
        .filter(function(selection) {
          return selection.selected;
        })
        .map(
          function(selection) {
            const matchingBaggage = this.findMatchingBaggage(
              selection.id,
              origCode
            );
            return matchingBaggage
              ? matchingBaggage.description.split(" for ")[0]
              : "";
          }.bind(this)
        )
        .filter(function(desc) {
          return desc && desc.length > 0;
        });

      return descriptions.join(", ");
    },

    findMatchingBaggage(baggageId, origCode) {
      const isFirstLeg =
        origCode ===
        this.priceResponse.itineraries[0].odoList[0].segments[0].origCode;

      return this.priceResponse.baggageOptions.allBaggage.find(function(bag) {
        return (
          bag.id === baggageId &&
          ((isFirstLeg && bag.direction === "OUTBOUND") ||
            (!isFirstLeg && bag.direction === "INBOUND"))
        );
      });
    },
  },
};
</script>

<style scoped>
.baggage-display {
  width: 100%;
}

.pair {
  display: flex;
  justify-content: space-between;
  margin-bottom: 8px;
}

.key {
  text-align: left;
  opacity: 0.7;
}

.value {
  text-align: left;
  opacity: 1;
  max-width: 70%;
  width: -webkit-fill-available;
}

.breakdown {
  margin-bottom: 30px;
}
</style>
